import { TreeModule } from '@ali-hm/angular-tree-component';
import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import localeThai from '@angular/common/locales/th';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerConfig,
  NgbDatepickerI18n,
  NgbDatepickerModule,
  NgbModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgxCaptchaModule } from 'ngx-captcha';
import { NgxResizeObserverModule } from 'ngx-resize-observer';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AdfsCallbackComponent } from '@core/components/adfs-callback/adfs-callback.component';
import { HomeComponent } from '@core/components/home/home.component';
import { LoginComponent } from '@core/components/login/login.component';
import { MaintenanceComponent } from '@core/components/maintenance/maintenance.component';
import { NavbarComponent } from '@core/components/navbar/navbar.component';
import { PageNotFoundComponent } from '@core/components/page-not-found/page-not-found.component';
import { SidebarComponent } from '@core/components/sidebar/sidebar.component';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { JwtInterceptor } from '@core/interceptors/jwt.interceptor';
import {
  ExtendedNgbDateAdapter,
  ExtendedNgbDateParserFormatter,
} from './dateparser';
import { ExtendedNgbDatepickerConfig } from './datepicker-config';
import { NgbDatepickerI18nBuddhist } from './datepicker-i18n';
import { BuddhistDatePipe } from '@shared/pipes/date.pipe';
import { GlobalErrorHandler } from '@shared/service/error-handler';
import { SharedModule } from '@shared/shared.module';
import { ApprovalRequestState } from './store/approval-request/approval-request.state';
import { AuthState } from './store/auth/auth.state';
import { LoaderState } from './store/loader/loader.state';
import { MemoPreviewState } from './store/memo-preview/memo-preview.state';
import {
  MemoCreationState,
  MemoPasswordPopupState,
} from './store/memo/memo.state';
import { NdidState } from './store/ndid/ndid.state';
import { OtpState } from './store/otp/otp.state';
import { UserState } from './store/users/users.state';
import { MemoPasswordInterceptor } from '@core/interceptors/memo-password.interceptor';
import { SvgPagePlaceholderComponent } from '@shared/components/svg-page-placeholder/svg-page-placeholder.component';
import { ModalWrongPasswordAttemptComponent } from '@shared/components/modal-wrong-password-attempt/modal-wrong-password-attempt.component';
import { AccountLockedComponent } from '@core/components/account-locked/account-locked.component';
import { LangSelectorComponent } from '@shared/components/lang-selector/lang-selector.component';
import { ForgetPasswordComponent } from '@core/components/forget-password/forget-password.component';
import { DashboardContainerComponent } from '@core/components/dashboard-container/dashboard-container.component';
import { ResetPasswordModule } from './modules/reset-password/reset-password.module';
import { NewFeedAnnouncementState } from './store/new-feed-announcement/new-feed-announcement.state';
import { SavedDashboardModule } from '@core/components/dashboard-container/saved-dashboard.module';
import { Chart, registerables } from 'chart.js';
import dataLabelsPlugin from 'chartjs-plugin-datalabels';

import { CustomSentryErrorHandler } from '@shared/service/sentry-error-handler';

registerLocaleData(localeThai);

Chart.register(
  ...registerables,
  dataLabelsPlugin,
  // zoomPlugin,
  // scrollbarPlugin,
);

export const createTranslateLoader = (
  http: HttpClient,
): TranslateHttpLoader =>
  new TranslateHttpLoader(http, './assets/i18n/', '.json');

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    AdfsCallbackComponent,
    HomeComponent,
    PageNotFoundComponent,
    MaintenanceComponent,
    SidebarComponent,
    NavbarComponent,
    AccountLockedComponent,
    ForgetPasswordComponent,
    DashboardContainerComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    NgSelectModule,
    FormsModule,
    SweetAlert2Module.forRoot(),
    SharedModule,
    NgbModule,
    TreeModule,
    NgbDatepickerModule,
    NgxsModule.forRoot(
      [
        UserState,
        AuthState,
        MemoCreationState,
        LoaderState,
        OtpState,
        NdidState,
        MemoPreviewState,
        ApprovalRequestState,
        MemoPasswordPopupState,
        NewFeedAnnouncementState,
      ],
      {
        developmentMode: false,
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      },
    ),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
      isolate: false,
    }),
    BsDatepickerModule.forRoot(),
    NgxCaptchaModule,
    NgIdleKeepaliveModule.forRoot(),
    NgxResizeObserverModule,
    SvgPagePlaceholderComponent,
    ModalWrongPasswordAttemptComponent,
    LangSelectorComponent,
    ResetPasswordModule,
    SavedDashboardModule,
  ],
  providers: [
    provideAnimations(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MemoPasswordInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: NgbDatepickerI18n,
      useClass: NgbDatepickerI18nBuddhist,
    },
    {
      provide: NgbDatepickerConfig,
      useClass: ExtendedNgbDatepickerConfig,
    },
    { provide: NgbDateAdapter, useClass: ExtendedNgbDateAdapter },
    {
      provide: NgbDateParserFormatter,
      useClass: ExtendedNgbDateParserFormatter,
    },
    BuddhistDatePipe,
    {
      provide: ErrorHandler,
      useClass: CustomSentryErrorHandler,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
